import { useState, useEffect } from 'react';
import { UseCheckoutResult, CheckoutDataWithAddresses } from '../types';
import { CheckoutService } from '../services/checkoutService';
import { AddressService } from '../services/addressService';
import { useUser } from '../contexts/UserContext';

export function useCheckout(): UseCheckoutResult {
  const [checkoutData, setCheckoutData] = useState<CheckoutDataWithAddresses | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, promoCodes } = useUser();

  useEffect(() => {
    let isMounted = true;

    const fetchCheckoutData = async () => {
      // Wait for auth state to resolve so guestPrefix() returns the correct value
      if (!window.__USER_FETCHED__) {
        await new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (window.__USER_FETCHED__) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (!isMounted) return;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Fetch checkout data (without savedShippingLocations and promoCodes)
        const fetchedData = await CheckoutService.getCheckoutData();

        // Initialize with empty savedShippingLocations and promoCodes from UserContext
        const checkoutWithExtras: CheckoutDataWithAddresses = {
          ...fetchedData,
          savedShippingLocations: [],
          promoCodes: promoCodes,
        };

        // Fetch addresses separately if user is available
        if (user?.id) {
          try {
            const addressResponse = await AddressService.getUserAddresses(user.id);
            checkoutWithExtras.savedShippingLocations = addressResponse.data;
          } catch (addressError) {
            console.warn('Failed to fetch user addresses, using empty addresses:', addressError);
            // Keep empty array if address fetch fails
          }
        }

        if (isMounted) {
          setCheckoutData(checkoutWithExtras);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load checkout data';
        if (isMounted) {
          setError(errorMessage);
        }
        console.error('Error fetching checkout data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCheckoutData();

    return () => {
      isMounted = false;
    };
  }, [user?.id, promoCodes]);

  return {
    checkoutData,
    loading,
    error,
  };
}
